import { type MigrationSetInfo, parseTags } from '@modelviewer/core';
import { ChevronRight, ClipboardList, ClipboardPaste, Crosshair, Database, Download, FileCode2, FilePlus2, FileText, Files, FlaskConical, FolderOpen, GitBranchPlus, GitCommitVertical, History, Layers, Network, PenLine, Route, Search, Table2, TriangleAlert, Upload, Waypoints } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { cn } from '../lib/cn';
import { exportAllSketches, importFiles } from '../sketch/files';
import { openPasteDialog } from '../sketch/PasteDialog';
import { MODEL_TABS, openCodeMap, openDiagram, openEndpointMap, openFiles, openGraph, openNote, openSketch, openSourceFile, openTestMap, openTimeline, tabKind, useActiveTab } from '../state/dock';
import { useFiles } from '../state/files';
import { refLabel } from '../files/format';
import { mappedFiles, revealCode, useCodeMap } from '../state/codemap';
import { useCodeSettings } from '../code/settings';
import { isFolderNote, isFolderSketch, useFolderProblems } from '../state/folderItems';
import { createNote, useCaseTemplate } from '../state/notes';
import { baseLabel, branchSketch } from '../state/sketches';
import { refKey, revealEntity, useWorkspace, type ModelRef } from '../state/workspace';

export function migrationDate(timestamp: string): string {
  const d = new Date(Date.UTC(+timestamp.slice(0, 4), +timestamp.slice(4, 6) - 1, +timestamp.slice(6, 8)));
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

function SectionHeader({ title, open, onToggle, extra }: { title: string; open: boolean; onToggle: () => void; extra?: React.ReactNode }) {
  return (
    <div className="flex h-7 items-center pr-2 pointer-coarse:h-10">
      <button onClick={onToggle} className="flex flex-1 items-center gap-1 self-stretch px-2 text-[11px] font-semibold tracking-wider text-faint uppercase hover:text-muted" aria-expanded={open}>
        <ChevronRight size={12} className={cn('transition-transform', open && 'rotate-90')} />
        {title}
      </button>
      {extra}
    </div>
  );
}

export function TreeItem(props: {
  icon: React.ReactNode;
  label: React.ReactNode;
  hint?: React.ReactNode;
  depth: number;
  active?: boolean;
  expanded?: boolean;
  onClick: () => void;
  onDoubleClick?: () => void;
  onToggle?: () => void;
  title?: string;
  /** A button shown when the item is hovered or focused. */
  action?: React.ReactNode;
}) {
  return (
    <div
      role="treeitem"
      aria-selected={props.active}
      aria-expanded={props.expanded}
      tabIndex={0}
      title={props.title}
      onClick={props.onClick}
      onDoubleClick={props.onDoubleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter') props.onClick();
        if (e.key === 'ArrowRight' && props.expanded === false) props.onToggle?.();
        if (e.key === 'ArrowLeft' && props.expanded === true) props.onToggle?.();
      }}
      className={cn(
        'flex h-[26px] cursor-default items-center gap-1.5 rounded-md pr-2 outline-none select-none focus-visible:ring-1 focus-visible:ring-accent pointer-coarse:h-9',
        'group',
        props.active ? 'bg-active text-text' : 'text-muted hover:bg-hover hover:text-text',
      )}
      style={{ paddingLeft: 8 + props.depth * 14 }}
    >
      {props.onToggle ? (
        <button
          tabIndex={-1}
          onClick={(e) => {
            e.stopPropagation();
            props.onToggle!();
          }}
          className="-ml-1 flex h-4 w-4 items-center justify-center pointer-coarse:-my-2 pointer-coarse:h-8 pointer-coarse:w-6"
          aria-label={props.expanded ? 'Collapse' : 'Expand'}
        >
          <ChevronRight size={12} className={cn('transition-transform', props.expanded && 'rotate-90')} />
        </button>
      ) : (
        <span className="w-3" />
      )}
      <span className="flex-none text-faint">{props.icon}</span>
      <span className="min-w-0 flex-1 truncate">{props.label}</span>
      {props.hint && <span className="flex-none text-[11px] text-faint">{props.hint}</span>}
      {props.action && <span className="hidden flex-none group-focus-within:flex group-hover:flex pointer-coarse:flex">{props.action}</span>}
    </div>
  );
}

function ContextTree({ set }: { set: MigrationSetInfo }) {
  const [open, setOpen] = useState(true);
  const [showMigrations, setShowMigrations] = useState(set.migrations.length <= 15);
  const [filter, setFilter] = useState('');
  // Marked by the tab in front: a model's diagram, or the timeline (and the migration it is at).
  const tab = useActiveTab((s) => s.id);
  const step = useWorkspace((s) => (s.activeStep?.setKey === set.key ? s.activeStep.stepId : null));
  const timelineInFront = tab === `timeline:${set.key}`;
  const isActive = (migrationId: string | null) => tab === `diagram:${refKey({ setKey: set.key, migrationId })}` || (migrationId !== null && timelineInFront && step === migrationId);
  const migrations = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const list = q ? set.migrations.filter((m) => m.id.toLowerCase().includes(q)) : set.migrations;
    return [...list].reverse();
  }, [set.migrations, filter]);

  return (
    <div role="group">
      <TreeItem
        depth={0}
        icon={<Database size={14} />}
        label={<span className="font-medium text-text">{set.contextName}</span>}
        title={`${set.contextName} in ${set.folder}`}
        expanded={open}
        onToggle={() => setOpen(!open)}
        onClick={() => setOpen(!open)}
      />
      {open && (
        <>
          <TreeItem
            depth={1}
            icon={<Layers size={14} />}
            label="Current model"
            hint={set.snapshotPath ? undefined : 'no snapshot'}
            active={isActive(null)}
            onClick={() => openDiagram({ setKey: set.key, migrationId: null })}
          />
          <TreeItem
            depth={1}
            icon={<History size={14} />}
            label="History"
            hint={set.migrations.length ? undefined : 'no migrations'}
            active={timelineInFront}
            onClick={() => openTimeline(set.key)}
            title="Step through the migrations and see what each one changed"
          />
          <TreeItem
            depth={1}
            icon={<GitCommitVertical size={14} />}
            label="Migrations"
            hint={set.migrations.length}
            expanded={showMigrations}
            onToggle={() => setShowMigrations(!showMigrations)}
            onClick={() => setShowMigrations(!showMigrations)}
          />
          {showMigrations && (
            <>
              {set.migrations.length > 15 && (
                <label className="mx-2 my-1 flex items-center gap-1.5 rounded-md border border-border px-2" style={{ marginLeft: 36 }}>
                  <Search size={12} className="text-faint" />
                  <input
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    placeholder="Filter migrations"
                    className="h-6 min-w-0 flex-1 bg-transparent text-[12px] outline-none placeholder:text-faint pointer-coarse:h-9"
                  />
                </label>
              )}
              {migrations.map((m) => (
                <TreeItem
                  key={m.id}
                  depth={2}
                  icon={<span className={cn('block h-1.5 w-1.5 rounded-full', m.designerPath ? 'bg-accent' : 'bg-faint')} />}
                  label={m.name}
                  title={`${m.name}, ${migrationDate(m.timestamp)}${m.designerPath ? '' : ' (no Designer file)'}`}
                  active={isActive(m.id)}
                  onClick={() => openTimeline(set.key, m.id)}
                />
              ))}
            </>
          )}
        </>
      )}
    </div>
  );
}

function HeaderButton({ label, onClick, children }: { label: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      title={label}
      aria-label={label}
      className="flex h-6 w-6 items-center justify-center rounded text-faint hover:bg-hover hover:text-text pointer-coarse:h-9 pointer-coarse:w-9"
    >
      {children}
    </button>
  );
}

/** Starts a sketch from the model in the active tab (or the largest context's current model). */
export async function branchFromActive(): Promise<void> {
  const { activeRef, discovery } = useWorkspace.getState();
  const first = discovery ? [...discovery.sets].sort((a, b) => b.migrations.length - a.migrations.length)[0] : undefined;
  const from = activeRef ?? (first ? { setKey: first.key, migrationId: null } : null);
  if (!from) return;
  const sketch = await branchSketch(from);
  if (sketch) openSketch(sketch.id);
}

/** Files in `.modelviewer/` that could not be read. */
function FolderProblems({ kind }: { kind: 'sketch' | 'note' }) {
  const problems = useFolderProblems();
  const list = Object.entries(problems).filter(([path]) => (kind === 'sketch' ? isFolderSketch(path) : isFolderNote(path)));
  return (
    <>
      {list.map(([path, problem]) => (
        <TreeItem
          key={path}
          depth={0}
          icon={<TriangleAlert size={14} className="text-removed" />}
          label={<span className="text-removed">{path.slice(path.lastIndexOf('/') + 1)}</span>}
          title={`${path}: ${problem}`}
          hint="invalid"
          onClick={() => openSourceFile(path)}
        />
      ))}
    </>
  );
}

function SketchList() {
  const sketches = useWorkspace((s) => s.sketches);
  const tab = useActiveTab((s) => s.id);
  const problems = useFolderProblems();
  // Files in the folder first (what the agent wrote), then the browser's own sketches.
  const list = useMemo(() => Object.values(sketches).sort((a, b) => Number(!!b.origin) - Number(!!a.origin) || b.updated - a.updated), [sketches]);
  if (list.length === 0 && !Object.keys(problems).some(isFolderSketch)) {
    return (
      <p className="mx-2 rounded-md border border-dashed border-border p-3 text-[12px] leading-relaxed text-muted">
        No sketches yet. Branch one from a model with <GitBranchPlus size={12} className="inline align-[-2px]" />, import an <code className="font-mono text-text">.efmodel.json</code> file, or paste one.
      </p>
    );
  }
  return (
    <div role="tree" aria-label="Sketches">
      {list.map((s) => (
        <TreeItem
          key={s.id}
          depth={0}
          icon={s.origin ? <FolderOpen size={14} /> : <PenLine size={14} />}
          label={s.doc.title}
          hint={s.origin ? 'folder' : undefined}
          title={[s.doc.title, s.origin ? s.origin.path : null, baseLabel(s) ? `from ${baseLabel(s)}` : null].filter(Boolean).join(', ')}
          active={tab === `sketch:${s.id}`}
          onClick={() => openSketch(s.id)}
        />
      ))}
      <FolderProblems kind="sketch" />
    </div>
  );
}

export async function newNote(kind: 'note' | 'use case' = 'note'): Promise<void> {
  const note = kind === 'note' ? await createNote('Untitled') : await createNote('New use case', useCaseTemplate('New use case'));
  if (note) openNote(note.id);
}

function NoteList() {
  const notes = useWorkspace((s) => s.notes);
  const tab = useActiveTab((s) => s.id);
  const [tag, setTag] = useState<string | null>(null);
  const withTags = useMemo(() => Object.values(notes).map((n) => ({ note: n, tags: parseTags(n.body) })), [notes]);
  const tags = useMemo(() => {
    const counts = new Map<string, number>();
    for (const { tags } of withTags) for (const t of tags) counts.set(t, (counts.get(t) ?? 0) + 1);
    return [...counts].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }, [withTags]);
  const list = withTags.filter((x) => !tag || x.tags.includes(tag)).sort((a, b) => Number(!!b.note.origin) - Number(!!a.note.origin) || b.note.updated - a.note.updated);
  if (withTags.length === 0) {
    return (
      <p className="mx-2 rounded-md border border-dashed border-border p-3 text-[12px] leading-relaxed text-muted">
        No notes yet. Notes can link to entities, sketches and migrations with [[Name]], and collect #tags.
      </p>
    );
  }
  return (
    <>
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-1 px-2 pb-1.5" aria-label="Tags">
          {tags.map(([t, n]) => (
            <button
              key={t}
              onClick={() => setTag(tag === t ? null : t)}
              aria-pressed={tag === t}
              className={cn('rounded-full px-2 text-[11.5px] leading-5', tag === t ? 'bg-accent text-accent-fg' : 'bg-accent-soft text-accent hover:bg-active')}
            >
              #{t} <span className="opacity-60">{n}</span>
            </button>
          ))}
        </div>
      )}
      <div role="tree" aria-label="Notes">
        {list.map(({ note }) => (
          <TreeItem
            key={note.id}
            depth={0}
            icon={note.origin ? <FolderOpen size={14} /> : <FileText size={14} />}
            label={note.title}
            hint={note.origin ? 'folder' : undefined}
            title={note.origin?.path}
            active={tab === `note:${note.id}`}
            onClick={() => openNote(note.id)}
          />
        ))}
        <FolderProblems kind="note" />
      </div>
    </>
  );
}

function EntityList({ activeRef }: { activeRef: ModelRef }) {
  const model = useWorkspace((s) => s.models[refKey(activeRef)]);
  const selection = useWorkspace((s) => s.selection);
  const [filter, setFilter] = useState('');
  const entities = useMemo(() => {
    if (!model) return [];
    const q = filter.trim().toLowerCase();
    return Object.entries(model.result.doc.model.entities)
      .filter(([k]) => !q || k.toLowerCase().includes(q))
      .sort(([a], [b]) => a.localeCompare(b));
  }, [model, filter]);
  if (!model) return <p className="px-4 py-2 text-[12px] text-faint">Reading…</p>;
  const selectedKey = selection && refKey(selection.ref) === refKey(activeRef) && selection.value.kind === 'entity' ? selection.value.key : null;
  return (
    <>
      <label className="mx-2 mb-1 flex items-center gap-1.5 rounded-md border border-border px-2">
        <Search size={12} className="text-faint" />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter entities"
          aria-label="Filter entities"
          className="h-6 min-w-0 flex-1 bg-transparent text-[12px] outline-none placeholder:text-faint pointer-coarse:h-9"
        />
      </label>
      <div role="tree">
        {entities.map(([key, e]) => (
          <TreeItem
            key={key}
            depth={0}
            icon={<Table2 size={14} />}
            label={key}
            hint={e.base ? `: ${e.base}` : e.keyless ? 'keyless' : undefined}
            active={selectedKey === key}
            onClick={() => revealEntity(activeRef, key)}
            onDoubleClick={() => revealEntity(activeRef, key, { focus: true })}
            action={
              <button
                tabIndex={-1}
                onClick={(e) => {
                  e.stopPropagation();
                  revealEntity(activeRef, key, { focus: true });
                }}
                className="-mr-1 flex h-5 w-5 items-center justify-center rounded text-faint hover:bg-active hover:text-accent pointer-coarse:h-8 pointer-coarse:w-8"
                title={`Focus on ${key}: show only it and what it relates to`}
                aria-label={`Focus on ${key}`}
              >
                <Crosshair size={12} />
              </button>
            }
          />
        ))}
      </div>
    </>
  );
}

/** The files on the code map, filtered by path or by the types they declare. */
function CodeFileList() {
  const map = useCodeMap((s) => s.map);
  const selection = useCodeMap((s) => s.selection);
  const hideTests = useCodeSettings((s) => s.tests === 'hide');
  const [filter, setFilter] = useState('');
  const files = useMemo(() => {
    if (!map) return [];
    const q = filter.trim().toLowerCase();
    const typeNames = new Map(map.types.map((t) => [t.id, t.name.toLowerCase()]));
    // The files on the map: test files only while the map shows them.
    return map.files.filter((f) => f.types.length && !(hideTests && f.test) && (!q || f.path.toLowerCase().includes(q) || f.types.some((t) => typeNames.get(t)?.includes(q))));
  }, [map, filter, hideTests]);
  if (!map) return <p className="px-4 py-2 text-[12px] text-faint">Reading…</p>;
  const selectedFile = selection?.kind === 'file' ? selection.path : null;
  return (
    <>
      <label className="mx-2 mb-1 flex items-center gap-1.5 rounded-md border border-border px-2">
        <Search size={12} className="text-faint" />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter files and types"
          aria-label="Filter files and types"
          className="h-6 min-w-0 flex-1 bg-transparent text-[12px] outline-none placeholder:text-faint pointer-coarse:h-9"
        />
      </label>
      <div role="tree" aria-label="Code files">
        {files.slice(0, 500).map((f) => {
          const name = f.path.slice(f.path.lastIndexOf('/') + 1);
          const folder = f.path.includes('/') ? f.path.slice(0, f.path.lastIndexOf('/')) : '';
          return (
            <TreeItem
              key={f.path}
              depth={0}
              icon={f.problems ? <TriangleAlert size={14} className="text-changed" /> : <FileCode2 size={14} />}
              label={name}
              hint={folder.slice(folder.lastIndexOf('/') + 1) || undefined}
              title={f.path}
              active={selectedFile === f.path}
              onClick={() => revealCode(f.path)}
              onDoubleClick={() => revealCode(f.path, { focus: true })}
              action={
                <button
                  tabIndex={-1}
                  onClick={(e) => {
                    e.stopPropagation();
                    revealCode(f.path, { focus: true });
                  }}
                  className="-mr-1 flex h-5 w-5 items-center justify-center rounded text-faint hover:bg-active hover:text-accent pointer-coarse:h-8 pointer-coarse:w-8"
                  title={`Focus on ${name}: show only it and what it calls or is called by`}
                  aria-label={`Focus on ${name}`}
                >
                  <Crosshair size={12} />
                </button>
              }
            />
          );
        })}
        {files.length > 500 && <p className="px-3 py-1 text-[12px] text-faint">{files.length - 500} more; filter to find them.</p>}
      </div>
    </>
  );
}

function CodeSection() {
  const files = useWorkspace((s) => s.files);
  const view = useCodeMap((s) => s.view);
  const endpoints = useCodeMap((s) => s.map?.endpoints.length);
  const tests = useCodeMap((s) => s.map?.tests.length);
  const hideTests = useCodeSettings((s) => s.tests === 'hide');
  // The files on the map: without test files while they are hidden.
  const mapped = useCodeMap((s) => (s.map ? s.map.stats.files - (hideTests ? s.map.files.filter((f) => f.test).length : 0) : undefined));
  const filesActive = useActiveTab((s) => s.id === 'files');
  const branch = useFiles((s) => (s.repo.state === 'ready' ? (s.head?.ref ? refLabel(s.head.ref) : 'detached') : undefined));
  const [open, setOpen] = useState(true);
  // Counted from the file list; generated files are left out once they are read.
  const count = useMemo(() => (files.length ? mappedFiles().length : 0), [files]);
  return (
    <>
      <SectionHeader title="Code" open={open} onToggle={() => setOpen(!open)} />
      {open && (
        <div className="px-1.5 pb-3">
          <div role="tree" aria-label="Code">
            <TreeItem
              depth={0}
              icon={<Files size={14} />}
              label="Files"
              hint={branch}
              title="Every file, and what each commit changed"
              active={filesActive}
              onClick={openFiles}
            />
            {count > 0 && (
              <>
                <TreeItem
                  depth={0}
                  icon={<Waypoints size={14} />}
                  label="Code map"
                  hint={mapped !== undefined ? `${mapped} file${mapped === 1 ? '' : 's'}` : `${count} C# file${count === 1 ? '' : 's'}`}
                  title="Files, their types and members, and which members call which"
                  active={view === 'code'}
                  onClick={openCodeMap}
                />
                <TreeItem
                  depth={0}
                  icon={<Route size={14} />}
                  label="Endpoints"
                  hint={endpoints !== undefined ? `${endpoints}` : undefined}
                  title="Each HTTP endpoint and the files, members and data it reaches"
                  active={view === 'endpoints'}
                  onClick={openEndpointMap}
                />
                <TreeItem
                  depth={0}
                  icon={<FlaskConical size={14} />}
                  label="Tests"
                  hint={tests !== undefined ? `${tests}` : undefined}
                  title="Each test and the files, members and data it reaches, and what it fakes"
                  active={view === 'tests'}
                  onClick={openTestMap}
                />
              </>
            )}
          </div>
          {view === 'code' && <CodeFileList />}
        </div>
      )}
    </>
  );
}

export function Explorer() {
  const discovery = useWorkspace((s) => s.discovery);
  const activeRef = useWorkspace((s) => s.activeRef);
  const busy = useWorkspace((s) => s.busy);
  // The entities of the model the tab in front shows.
  const modelInFront = useActiveTab((s) => MODEL_TABS.has(tabKind(s.subject) ?? ''));
  const [contextsOpen, setContextsOpen] = useState(true);
  const [sketchesOpen, setSketchesOpen] = useState(true);
  const [notesOpen, setNotesOpen] = useState(true);
  const [entitiesOpen, setEntitiesOpen] = useState(true);
  const fileInput = useRef<HTMLInputElement>(null);

  return (
    <nav className="flex h-full flex-col overflow-hidden" aria-label="Explorer">
      <div className="min-h-0 flex-1 overflow-y-auto py-2">
        <SectionHeader title="Contexts" open={contextsOpen} onToggle={() => setContextsOpen(!contextsOpen)} />
        {contextsOpen && (
          <div role="tree" className="px-1.5 pb-3">
            {!discovery && busy && <p className="px-3 py-1 text-[12px] text-faint">{busy}</p>}
            {discovery?.sets.length === 0 && (
              <div className="mx-2 rounded-md border border-dashed border-border p-3 text-[12px] leading-relaxed text-muted">
                No EF Core migrations found. modelviewer looks for a folder with a <code className="font-mono text-text">*ModelSnapshot.cs</code> file
                and migrations named like <code className="font-mono text-text">20240101120000_Initial.cs</code>.
                {mappedFiles().length > 0 && <> The code map below shows the C# code.</>}
              </div>
            )}
            {discovery?.sets.map((set) => <ContextTree key={set.key} set={set} />)}
            {discovery && discovery.unassigned.length > 0 && (
              <p className="px-3 py-1 text-[12px] text-changed">
                {discovery.unassigned.length} migration{discovery.unassigned.length > 1 ? 's' : ''} could not be matched to a context.
              </p>
            )}
          </div>
        )}
        {discovery && <CodeSection />}
        {discovery && (
          <>
            <SectionHeader
              title="Sketches"
              open={sketchesOpen}
              onToggle={() => setSketchesOpen(!sketchesOpen)}
              extra={
                <span className="flex items-center">
                  <HeaderButton label="New sketch from the model in view" onClick={() => void branchFromActive()}>
                    <GitBranchPlus size={13} />
                  </HeaderButton>
                  <HeaderButton label="Import .efmodel.json files or a zip" onClick={() => fileInput.current?.click()}>
                    <Upload size={13} />
                  </HeaderButton>
                  <HeaderButton label="Paste a sketch" onClick={openPasteDialog}>
                    <ClipboardPaste size={13} />
                  </HeaderButton>
                  <HeaderButton label="Download all sketches as a zip" onClick={() => void exportAllSketches()}>
                    <Download size={13} />
                  </HeaderButton>
                </span>
              }
            />
            <input
              ref={fileInput}
              type="file"
              accept=".json,.zip"
              multiple
              hidden
              data-testid="sketch-import"
              onChange={(e) => {
                const files = [...(e.target.files ?? [])];
                e.target.value = '';
                void importFiles(files).then((added) => added[0] && openSketch(added[0].id));
              }}
            />
            {sketchesOpen && (
              <div className="px-1.5 pb-3">
                <SketchList />
              </div>
            )}
          </>
        )}
        {discovery && (
          <>
            <SectionHeader
              title="Notes"
              open={notesOpen}
              onToggle={() => setNotesOpen(!notesOpen)}
              extra={
                <span className="flex items-center">
                  <HeaderButton label="New note" onClick={() => void newNote()}>
                    <FilePlus2 size={13} />
                  </HeaderButton>
                  <HeaderButton label="New use case (a note to decide on a design)" onClick={() => void newNote('use case')}>
                    <ClipboardList size={13} />
                  </HeaderButton>
                  <HeaderButton label="Graph of notes, sketches and links" onClick={openGraph}>
                    <Network size={13} />
                  </HeaderButton>
                </span>
              }
            />
            {notesOpen && (
              <div className="px-1.5 pb-3">
                <NoteList />
              </div>
            )}
          </>
        )}
        {activeRef && modelInFront && (
          <>
            <SectionHeader title="Entities" open={entitiesOpen} onToggle={() => setEntitiesOpen(!entitiesOpen)} />
            {entitiesOpen && (
              <div className="px-1.5 pb-3">
                <EntityList activeRef={activeRef} />
              </div>
            )}
          </>
        )}
      </div>
    </nav>
  );
}
